import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardFooter } from './ui/card';
import { Star, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatingStars = ({ value, size = 'default' }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${
          size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'
        } ${i <= value ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300'}`}
      />
    );
  }
  return <div className="flex items-center space-x-0.5">{stars}</div>;
};

const ReviewCard = ({ review, index }) => (
  <Card
    className="flex flex-col justify-between h-full bg-white border-0 shadow-md rounded-3xl"
    data-testid={`review-card-${index}`}
  >
    <CardHeader className="flex flex-row items-start space-x-3 pb-4">
      {review.author.avatar_url && (
        <img
          src={review.author.avatar_url}
          alt={review.author.name}
          className="h-12 w-12 rounded-full object-cover"
        />
      )}
      <div className="space-y-2 flex-1">
        <div>
          {review.author.profile_url ? (
            <a
              href={review.author.profile_url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-neutral-900 hover:text-sky-600 hover:underline"
            >
              {review.author.name}
            </a>
          ) : (
            <p className="font-semibold text-neutral-900">{review.author.name}</p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <RatingStars value={review.rating} size="sm" />
          {review.relative_time_description && (
            <span className="text-xs text-neutral-500">
              {review.relative_time_description}
            </span>
          )}
        </div>
      </div>
    </CardHeader>

    <CardContent className="text-sm text-neutral-600 leading-relaxed pb-5">
      <p className="line-clamp-5">{review.text}</p>
    </CardContent>

    {review.owner_response && (
      <CardFooter className="mt-auto border-t border-neutral-100 pt-4">
        <div className="space-y-2 text-xs">
          <p className="font-semibold text-neutral-900">Antwort vom Inhaber</p>
          <p className="text-neutral-600 leading-relaxed">{review.owner_response.text}</p>
        </div>
      </CardFooter>
    )}
  </Card>
);

const GoogleReviewsSection = ({ layout = 'carousel' }) => {
  const isGrid = layout === 'grid';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startIdx, setStartIdx] = useState(0);
  const [perView, setPerView] = useState(3);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setPerView(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadReviews() {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${API}/reviews`, {
          signal: controller.signal,
        });

        setData(res.data);
      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        if (err.response && err.response.status === 503) {
          setError('not_configured');
        } else {
          setError(err.message || 'Unknown error');
        }
      } finally {
        setLoading(false);
      }
    }

    loadReviews();

    return () => {
      controller.abort();
    };
  }, []);

  const reviewsCount = data?.reviews?.length || 0;
  const totalSlides = Math.max(1, reviewsCount - perView + 1);

  const next = useCallback(() => setStartIdx((i) => (i + 1) % totalSlides), [totalSlides]);
  const prev = () => setStartIdx((i) => (i - 1 + totalSlides) % totalSlides);

  useEffect(() => {
    if (paused || reviewsCount <= perView || isGrid) return undefined;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [paused, reviewsCount, perView, next, isGrid]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500">Lädt Google Bewertungen...</p>
      </div>
    );
  }

  if (error === 'not_configured') {
    return null;
  }

  if (error) {
    return null;
  }

  if (!data) {
    return null;
  }

  const googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${data.place_id}`;

  return (
    <section className="py-8 md:py-12" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-3 mb-6">
          <div className="text-center space-y-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-sky-700">
              Was unsere Kunden sagen
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mx-auto">
              Echte Google Bewertungen von zufriedenen Familien
            </p>
          </div>

          <div className="flex flex-col items-center space-y-3">
            <div className="flex items-center space-x-3">
              <RatingStars value={Math.round(data.rating)} />
              <span className="text-2xl font-bold text-neutral-900" data-testid="overall-rating">
                {data.rating.toFixed(1)}
              </span>
              <span className="text-neutral-500">von 5</span>
            </div>
            <p className="text-sm text-neutral-600" data-testid="total-reviews">
              Basierend auf {data.user_ratings_total} Google Bewertungen
            </p>
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center space-x-2 text-sm font-medium text-sky-600 hover:text-sky-700 hover:underline"
              data-testid="view-all-google-reviews"
            >
              <MapPin className="h-4 w-4" />
              <span>Alle Bewertungen auf Google ansehen</span>
            </a>
          </div>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="reviews-slider"
        >
          {!isGrid && reviewsCount > perView && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Vorherige Bewertungen"
                className="hidden md:flex items-center justify-center absolute -left-2 top-1/2 -translate-y-1/2 bg-white text-sky-500 hover:bg-sky-500 hover:text-white rounded-full h-12 w-12 z-20 shadow-lg border border-neutral-200 transition-colors"
                data-testid="reviews-prev"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Nächste Bewertungen"
                className="hidden md:flex items-center justify-center absolute -right-2 top-1/2 -translate-y-1/2 bg-white text-sky-500 hover:bg-sky-500 hover:text-white rounded-full h-12 w-12 z-20 shadow-lg border border-neutral-200 transition-colors"
                data-testid="reviews-next"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {isGrid ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="reviews-grid">
              {data.reviews.map((review, index) => (
                <ReviewCard key={index} review={review} index={index} />
              ))}
            </div>
          ) : (
            <div className="overflow-hidden -mx-3 px-3 py-2">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${startIdx * (100 / perView)}%)` }}
              >
                {data.reviews.map((review, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 px-3 pb-4"
                    style={{ width: `${100 / perView}%` }}
                  >
                    <ReviewCard review={review} index={index} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination dots / counter (carousel only) */}
          {!isGrid && reviewsCount > perView && (
            totalSlides > 10 ? (
              <div className="flex justify-center items-center gap-3 mt-6" data-testid="reviews-pagination-counter">
                <span className="text-sm font-medium text-neutral-700 tabular-nums">
                  {startIdx + 1} / {totalSlides}
                </span>
                <span className="text-xs text-neutral-500">
                  ({reviewsCount} Bewertungen)
                </span>
              </div>
            ) : (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setStartIdx(i)}
                    aria-label={`Bewertung ${i + 1}`}
                    className={`h-2.5 rounded-full transition-all ${
                      i === startIdx ? 'w-8 bg-sky-500' : 'w-2.5 bg-neutral-300 hover:bg-neutral-400'
                    }`}
                  />
                ))}
              </div>
            )
          )}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-neutral-500">
            Bewertungen und Sternebewertungen werden von Google bereitgestellt.{' '}
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-sky-600"
            >
              Aktuelle Bewertungen auf Google Maps ansehen
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
